import React, { useState, useEffect } from 'react';
import { PageContainer } from '../components/layout/PageContainer';
import AssessmentSection from '../components/health-tools/sections/AssessmentSection';
import InsightsSection from '../components/health-tools/sections/InsightsSection';
import CareSection from '../components/health-tools/sections/CareSection';
import PredictiveSection from '../components/health-tools/sections/PredictiveSection';
import { Activity } from 'lucide-react';
import mockHealthData from '../data/mockHealthData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AIHealthCoach from '../components/health-tools/features/AIHealthCoach';
import AIMentalHealthBot from '../components/health-tools/features/AIMentalHealthBot';
import HealthChallenges from '../components/health-tools/features/HealthChallenges';
import NutritionTracker from '../components/health-tools/features/NutritionTracker';

// Define the tab types
type HealthToolTab = 'assessment' | 'insights' | 'care' | 'predictive' | 'engagement';

/**
 * HealthTools Page
 * 
 * A comprehensive health management dashboard with:
 * - Assessment tools (calculators, risk assessments)
 * - Health insights (scores, metrics, risk factors)
 * - Care coordination (recommendations, care plans)
 * - Predictive analytics (future risk projections)
 * - Engagement features (coaching, challenges, nutrition)
 * 
 * Organized in a tabular interface with a top navigation bar
 * and sidebar subheaders for each section.
 */
const HealthTools: React.FC = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState<HealthToolTab>('assessment');

  // Loading state to simulate data fetching
  const [loading, setLoading] = useState({
    assessment: true,
    insights: true,
    care: true,
    predictive: true,
    engagement: true
  });

  // Extract mock data for each section
  const { 
    assessmentData, 
    insightsData, 
    careData, 
    predictiveData,
    healthChallenges,
    mealHistory,
    nutritionRecommendations
  } = mockHealthData;

  // Simulate staggered loading of different sections
  useEffect(() => {
    // Simulate assessment data loading completion
    const assessmentTimer = setTimeout(() => {
      setLoading(prev => ({ ...prev, assessment: false }));
    }, 1200);

    // Simulate insights data loading completion
    const insightsTimer = setTimeout(() => {
      setLoading(prev => ({ ...prev, insights: false }));
    }, 1800);

    // Simulate care data loading completion
    const careTimer = setTimeout(() => {
      setLoading(prev => ({ ...prev, care: false }));
    }, 2400);

    // Simulate predictive data loading completion
    const predictiveTimer = setTimeout(() => {
      setLoading(prev => ({ ...prev, predictive: false }));
    }, 3000);

    // Simulate engagement data loading completion
    const engagementTimer = setTimeout(() => {
      setLoading(prev => ({ ...prev, engagement: false }));
    }, 3600);

    // Cleanup timers on unmount
    return () => {
      clearTimeout(assessmentTimer);
      clearTimeout(insightsTimer);
      clearTimeout(careTimer);
      clearTimeout(predictiveTimer);
      clearTimeout(engagementTimer);
    };
  }, []);

  // Define tab configuration directly here instead of relying on tabConfig
  const tabs: { [key in HealthToolTab]: { title: string; icon: React.FunctionComponent<any>; description: string } } = {
    assessment: { title: 'Assessment', icon: Activity, description: 'Evaluate your health status.' },
    insights: { title: 'Insights', icon: Activity, description: 'Understand your health metrics.' },
    care: { title: 'Care Plan', icon: Activity, description: 'Manage your care.' },
    predictive: { title: 'Predictions', icon: Activity, description: 'View future health projections.' },
    engagement: { title: 'Engagement', icon: Activity, description: 'Engage with health tools.' }
  };

  return (
    <PageContainer
      title="Health Tools"
      icon={<Activity className="h-5 w-5" />}
      description="Explore your health metrics and use tools to improve your wellbeing"
    >
      <div className="w-full h-[calc(100vh-8rem)]">
        {/* Main Content */}
        <div className="w-full overflow-auto">
          {/* Top Navigation Tabs */}
          <div className="sticky top-0 bg-background border-b z-10 p-4">
            <Tabs 
              defaultValue="assessment" 
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as HealthToolTab)}
              className="w-full"
            >
              <TabsList className="grid grid-cols-5 mb-2">
                {(Object.keys(tabs) as HealthToolTab[]).map((tab) => (
                  <TabsTrigger 
                    key={tab} 
                    value={tab}
                    className="flex items-center gap-1.5"
                  >
                    {React.createElement(tabs[tab].icon, { className: "h-4 w-4" })}
                    <span className="hidden sm:inline">{tabs[tab].title}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <div className="text-sm text-muted-foreground mb-4">
                {tabs[activeTab].description}
              </div>
              
              {/* Tab Content */}
              <TabsContent value="assessment" className="mt-0 pt-4 border-t">
                <AssessmentSection
                  calculators={assessmentData.calculators}
                  riskAssessments={assessmentData.riskAssessments}
                  screeningTools={assessmentData.screeningTools}
                  isLoading={loading.assessment}
                />
              </TabsContent>
              
              <TabsContent value="insights" className="mt-0 pt-4 border-t">
                <InsightsSection
                  healthScore={insightsData.healthScore}
                  riskFactors={insightsData.riskFactors}
                  insights={insightsData.insights}
                  isLoading={loading.insights}
                />
              </TabsContent>
              
              <TabsContent value="care" className="mt-0 pt-4 border-t">
                <CareSection
                  careRecommendations={careData.recommendations}
                  carePlan={careData.carePlan}
                  providers={careData.providers}
                  isLoading={loading.care}
                />
              </TabsContent>
              
              <TabsContent value="predictive" className="mt-0 pt-4 border-t">
                <PredictiveSection
                  riskPredictions={predictiveData.riskPredictions}
                  healthProjections={predictiveData.healthProjections}
                  interventionImpacts={predictiveData.interventionImpacts}
                  isLoading={loading.predictive}
                />
              </TabsContent>
              
              <TabsContent value="engagement" className="mt-0 pt-4 border-t">
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Health Engagement
                  </h2>
                  
                  <p className="text-gray-600 dark:text-gray-300">
                    Stay engaged with your health through challenges, coaching, mental health support, and nutrition tracking.
                  </p>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AIHealthCoach 
                      recentConversations={[
                        { id: 'conv1', title: 'Water Intake Discussion', preview: 'How have you been tracking with your daily water intake?', date: '2023-08-15' }
                      ]}
                      userGoals={[
                        'Drink 8 glasses of water daily',
                        'Walk 10,000 steps per day',
                        'Meditate for 10 minutes every morning',
                        'Reduce processed food intake'
                      ]}
                      className="h-full"
                    />
                    
                    <AIMentalHealthBot
                      messages={[
                        {
                          id: 'mental-1',
                          sender: 'bot',
                          content: 'Welcome to your mental wellness space. How are you feeling today?',
                          timestamp: new Date(Date.now() - 3600000),
                          resources: []
                        },
                        {
                          id: 'mental-2',
                          sender: 'user',
                          content: "I've been feeling a bit stressed with work lately.",
                          timestamp: new Date(Date.now() - 3500000)
                        },
                        {
                          id: 'mental-3',
                          sender: 'bot',
                          content: 'I understand. Work stress can be challenging. Would you like to try a quick breathing exercise or learn about some stress management techniques?',
                          timestamp: new Date(Date.now() - 3400000),
                          exercises: [
                            {
                              id: 'ex1',
                              title: '5-Minute Breathing Exercise',
                              description: 'A simple breathing technique to reduce anxiety and stress.',
                              duration: '5 minutes',
                              type: 'breathing'
                            }
                          ]
                        }
                      ]}
                      className="h-full"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <HealthChallenges 
                      challenges={healthChallenges.map(challenge => ({
                        ...challenge,
                        status: 'active',
                        name: challenge.title
                      }))} 
                    />
                    <NutritionTracker 
                      nutrition={{
                        calories: { current: 1250, goal: 2000 },
                        protein: { current: 45, goal: 75 },
                        carbs: { current: 130, goal: 250 },
                        fat: { current: 40, goal: 65 },
                        water: { current: 4, goal: 8 }
                      }}
                      mealHistory={mealHistory}
                      recommendations={nutritionRecommendations}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default HealthTools; 